import React, { useState, useEffect } from "react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from "recharts";
import { getNationalCensusData, getStateCensusData } from "../../utilityFunctions/DataloveUtil";
import { 
  Box, 
  Grid, 
  TextField, 
  MenuItem, 
  Typography, 
  FormControlLabel, 
  Checkbox,
  FormGroup,
  RadioGroup,
  Radio,
  Tabs,
  Tab,
  Paper
} from "@mui/material";
import CustomLoader from "../../components/CustomLoader";
import { useTranslation } from 'react-i18next';
import USMap from '../../components/USMap';
import CountyMap from '../../components/CountyMap';
import Colors from "../../components/ui/Colors";
import CensusBarChart from '../../components/CensusBarChart';

// Add these constants at the top of the file
const YEARS = [2015, 2016, 2017, 2018, 2019, 2020, 2021, 2022, 2023, 2024];
const STATES = [
  { code: "all", name: "All States" },
  { code: "01", name: "Alabama" },
  { code: "02", name: "Alaska" },
  { code: "04", name: "Arizona" },
  { code: "05", name: "Arkansas" },
  { code: "06", name: "California" },
  { code: "08", name: "Colorado" },
  { code: "09", name: "Connecticut" },
  { code: "10", name: "Delaware" },
  { code: "11", name: "District of Columbia" },
  { code: "12", name: "Florida" },
  { code: "13", name: "Georgia" },
  { code: "15", name: "Hawaii" },
  { code: "16", name: "Idaho" },
  { code: "17", name: "Illinois" },
  { code: "18", name: "Indiana" },
  { code: "19", name: "Iowa" },
  { code: "20", name: "Kansas" },
  { code: "21", name: "Kentucky" },
  { code: "22", name: "Louisiana" },
  { code: "23", name: "Maine" },
  { code: "24", name: "Maryland" },
  { code: "25", name: "Massachusetts" },
  { code: "26", name: "Michigan" },
  { code: "27", name: "Minnesota" },
  { code: "28", name: "Mississippi" },
  { code: "29", name: "Missouri" },
  { code: "30", name: "Montana" },
  { code: "31", name: "Nebraska" },
  { code: "32", name: "Nevada" },
  { code: "33", name: "New Hampshire" },
  { code: "34", name: "New Jersey" },
  { code: "35", name: "New Mexico" },
  { code: "36", name: "New York" },
  { code: "37", name: "North Carolina" },
  { code: "38", name: "North Dakota" },
  { code: "39", name: "Ohio" },
  { code: "40", name: "Oklahoma" },
  { code: "41", name: "Oregon" },
  { code: "42", name: "Pennsylvania" },
  { code: "44", name: "Rhode Island" },
  { code: "45", name: "South Carolina" },
  { code: "46", name: "South Dakota" },
  { code: "47", name: "Tennessee" },
  { code: "48", name: "Texas" },
  { code: "49", name: "Utah" },
  { code: "50", name: "Vermont" },
  { code: "51", name: "Virginia" },
  { code: "53", name: "Washington" },
  { code: "54", name: "West Virginia" },
  { code: "55", name: "Wisconsin" },
  { code: "56", name: "Wyoming" }
];

const CensusDashboard = ({ loginUser }) => {
  const [selectedState, setSelectedState] = useState("all");
  const [selectedYear, setSelectedYear] = useState(2017);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [breakdowns, setBreakdowns] = useState({ race: true, sex: false });
  const { t } = useTranslation();
  const [filteredData, setFilteredData] = useState(null);
  const [mapView, setMapView] = useState('state');
  const [selectedCounty, setSelectedCounty] = useState(null);
  const [countyData, setCountyData] = useState(null);

  useEffect(() => {
    fetchCensusData();
  }, [selectedState, selectedYear]);

  useEffect(() => {
    setSelectedCounty(null);
  }, [selectedState]);

  const fetchCensusData = async () => {
    setLoading(true);
    setError(null);
    try {
      console.log("Fetching census data for:", {
        state: selectedState,
        year: selectedYear,
        hasToken: !!loginUser?.jwtToken
      });

      // Get response from either national or state census data
      let response;
      if (selectedState === "all") {
        console.log("Calling getNationalCensusData endpoint");
        response = await getNationalCensusData({ year: selectedYear }, loginUser.jwtToken);
        console.log("National census response:", response);
      } else {
        console.log("Calling getStateCensusData endpoint");
        response = await getStateCensusData(
          { stateCode: selectedState, year: selectedYear },
          loginUser.jwtToken
        );
        console.log("State census response:", response);
      }

      if (!response) {
        console.error("No response received from census API");
        setError("No data received");
        return;
      }

      // If response is already in the correct format, use it directly
      if (Array.isArray(response) && response.length > 0) {
        console.log("Using response data directly:", response);
        setData(response);
        return;
      }

      // If we have a nested data structure
      if (response.data) {
        console.log("Processing nested response data:", response.data);
        const processedData = response.data.map(item => ({
          state: item.state?.padStart(2, '0'),
          population: Number(item.population || item.value || 0)
        }));
        console.log("Processed data:", processedData);
        setData(processedData);
        return;
      }

      console.error("Unexpected response format:", response);
      setError("Invalid data format received");

    } catch (error) {
      console.error("Error in fetchCensusData:", error);
      setError(error.message || "An error occurred while fetching data");
    } finally {
      setLoading(false);
    }
  };

  const handleCountySelect = (countyId) => {
    setSelectedCounty(countyId === selectedCounty ? null : countyId);
    
    if (countyId !== selectedCounty) {
      setMapView('county');
    }
  };

  const handleMapViewChange = (event, newView) => {
    setMapView(newView);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom sx={{ marginTop: "6rem", fontWeight: "bold", textAlign: "center" }}>
        {t('Census Data Analysis')}
      </Typography>
      
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            label={t('Select Year')}
            value={selectedYear}
            onChange={(e) => setSelectedYear(e.target.value)}
          >
            {YEARS.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid item xs={12} md={6}>
          <TextField
            select
            fullWidth
            label={t('Select State')}
            value={selectedState}
            onChange={(e) => setSelectedState(e.target.value)}
          >
            {STATES.map((state) => (
              <MenuItem key={state.code} value={state.code}>
                {state.name}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
      </Grid>

      <Paper sx={{ mb: 3 }}>
        <Tabs
          value={mapView}
          onChange={handleMapViewChange}
          centered
          textColor="primary"
          indicatorColor="primary"
        >
          <Tab value="state" label={t("State View")} />
          <Tab value="county" label={t("County View")} />
        </Tabs>
      </Paper>

      {mapView === 'state' ? (
        <USMap 
          data={data || []}
          selectedState={selectedState}
          onStateSelect={setSelectedState}
        />
      ) : (
        <CountyMap
          selectedYear={selectedYear}
          selectedState={selectedState}
          selectedCounty={selectedCounty}
          onCountySelect={handleCountySelect}
        />
      )}

      {selectedState !== "all" && mapView === 'state' && !loading && !error && data && data.length > 0 && (
        <>
          <Grid container spacing={3} sx={{ mb: 3, mt: 3 }}>
            <Grid item xs={12}>
              <FormGroup>
                <RadioGroup
                  row
                  value={breakdowns.race ? 'race' : breakdowns.sex ? 'sex' : ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setBreakdowns({
                      race: value === 'race',
                      sex: value === 'sex'
                    });
                  }}
                >
                  <FormControlLabel 
                    value="race"
                    control={<Radio />}
                    label={t('Show Race Breakdown')}
                  />
                  <FormControlLabel
                    value="sex" 
                    control={<Radio />}
                    label={t('Show Gender Breakdown')}
                  />
                </RadioGroup>
              </FormGroup>
            </Grid>
          </Grid>

          {!loading && !error && data?.length > 0 ? (
            <CensusBarChart 
              data={data} 
              breakdowns={breakdowns} 
              selectedStateName={STATES.find(s => s.code === selectedState)?.name}
            />
          ) : (
            <Typography sx={{ textAlign: 'center' }}>
              {loading ? 'Loading data...' : 'No demographic data available'}
            </Typography>
          )}
        </>
      )}

      {loading && <CustomLoader />}
      
      {error && (
        <Typography color="error" sx={{ textAlign: "center", mt: 2 }}>
          {error}
        </Typography>
      )}
    </Box>
  );
};

export default CensusDashboard;
